import { useEffect, useState } from "react";
import DropdownMenu from "../DropdownMenu";
import Modal from "../Modal";
import { useBulkActionsEntriesInspect } from "@/common/hooks/queries/bulkActions/useBulkActionsEntriesInspect";
import { BulkActionEntryInspectData } from "@/common/types/bulkActions/entryInspect.type";
import Button from "../Button";
import { useBulkActionsEntriesMine } from "@/common/hooks/queries/bulkActions/useBulkActionsEntriesMine";
import Table from "../Table";
import { bulkActionsMineEntriesTableColumns } from "@/common/constants/bulkActions/mineEntriesTableColumns.constant";
import { Mapper } from "@/common/services/mapper";
import { BulkActionsMineEntriesTransformer } from "@/common/services/bulkActions/BulkActionsMineEntriesTransformer";
import { bulkActionsGlobalColumnsFields } from "@/common/constants/bulkActions/globalColumnsFields.constant";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useBulkActionsEntriesRequestTemplate } from "@/common/hooks/queries/bulkActions/useBulkActionsEntriesRequestTemplate";
import { BulkActionGlobalColumnsValidationSchema } from "@/common/schemas/bulkActions/globalColumns.schema";
import { XIcon } from "lucide-react";
import { useBulkActionsEntriesMyLatestInProgress } from "@/common/hooks/queries/bulkActions/useBulkActionsEntriesMyLatestInProgress";
import { bulkActionsGlobalColumnsParser } from "@/common/services/bulkActions/bulkActionsGlobalColumnsParser";
import { useQueryClient } from "@tanstack/react-query";
import BulkActionTemplateModal from "../BulkActionTemplateModal";
import { Option } from "@/common/types/option";
import useUserPermissions from "@/common/hooks/useUserPermissions";

interface BulkActionNewEntryModalProps {
  operationKlassToCheckPermission?: string;
  options: {
    label: string;
    actionerKlass: string;
  }[];
  onEntryCompleted?: VoidFunction;
  globalColumnsDefaultValues?: {
    [key: string]: string | Option | undefined;
  };
}

const BulkActionNewEntryModal = ({
  operationKlassToCheckPermission,
  options,
  onEntryCompleted,
  globalColumnsDefaultValues,
}: BulkActionNewEntryModalProps) => {
  const queryClient = useQueryClient();

  const { hasUserPermissionWithActionerKlass } = useUserPermissions();

  const actionerKlassList = options.map(option => option.actionerKlass);
  const hasPermission = operationKlassToCheckPermission
    ? hasUserPermissionWithActionerKlass(operationKlassToCheckPermission, actionerKlassList)
    : true;

  const hasPermissionForActionerKlass = (actionerKlass: string) => {
    return operationKlassToCheckPermission
      ? hasUserPermissionWithActionerKlass(operationKlassToCheckPermission, [actionerKlass])
      : true;
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedActionerKlass, setSelectedActionerKlass] = useState<string>();
  const [selectedEntry, setSelectedEntry] = useState<string>();

  const formMethods = useForm<BulkActionGlobalColumnsValidationSchema>({
    defaultValues: globalColumnsDefaultValues,
  });

  const {
    handleSubmit,
    watch,
    reset,
  } = formMethods;

  const { data: entryInspect } = useBulkActionsEntriesInspect<BulkActionEntryInspectData>({
    filters: { actionerKlass: selectedActionerKlass },
  });

  const { data: mineEntries, isPending: isMineEntriesPending } = useBulkActionsEntriesMine({
    filters: {
      actionerKlass: selectedActionerKlass,
      globalColumns: bulkActionsGlobalColumnsParser(watch()),
    },
    mapper: Mapper.mapWithTransformer({ transformer: BulkActionsMineEntriesTransformer.tableTransformer(setSelectedEntry) }),
  });

  const { data: entryMyLatestInProgress } = useBulkActionsEntriesMyLatestInProgress<{ id?: string; }>({
    filters: {
      actionerKlass: selectedActionerKlass,
      timeZone: userTimeZone,
      globalColumns: bulkActionsGlobalColumnsParser(watch()),
    }
  });

  const { mutate: requestTemplate } = useBulkActionsEntriesRequestTemplate({
    actionerKlass: selectedActionerKlass,
    timeZone: userTimeZone,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bulkActionsEntriesMyLatestInProgress'] });

      setSelectedEntry(response.data.data.id);
      setSelectedActionerKlass(undefined);
    }
  });

  const onSubmit: SubmitHandler<BulkActionGlobalColumnsValidationSchema> = (globalColumns) => {
    requestTemplate(bulkActionsGlobalColumnsParser(globalColumns));
  };

  useEffect(() => {
    reset(globalColumnsDefaultValues);
  }, [...(globalColumnsDefaultValues ? Object.values(globalColumnsDefaultValues) : [])]);

  return (
    <>
      <Modal
        isOpen={!!selectedActionerKlass}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setSelectedActionerKlass(undefined);
          }
        }}
        Trigger={(
          <DropdownMenu
            triggerClassName="bg-primary-blue hover:bg-primary-blue/80 shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            triggerLabel="Ação em massa"
            options={options.map((option) => ({
              label: option.label,
              onClick: () => setSelectedActionerKlass(option.actionerKlass),
              disabled: !hasPermissionForActionerKlass(option.actionerKlass),
            }))}
            disabled={!hasPermission}
            operationKlassToCheckPermission={operationKlassToCheckPermission}
          />
        )}
        className="min-w-[860px] min-h-[640px] max-h-[640px] bg-black-900"
        body={(
          <FormProvider {...formMethods}>
            <div className="text-white-400 flex flex-col gap-6 h-full">
              <div className="flex items-center justify-between">
                <p className="text-lg text-white-800">{entryInspect?.title}</p>

                <Button className="p-2" variant="outline-error" onClick={() => setSelectedActionerKlass(undefined)}>
                  <XIcon strokeWidth={1.5} />
                </Button>
              </div>

              <div className="flex gap-6 h-full">
                <div className="w-1/2 flex flex-col">
                  {entryInspect.global_columns && Object.keys(entryInspect.global_columns).map((columnKey) => {
                    const FieldComponent = bulkActionsGlobalColumnsFields[columnKey as keyof typeof bulkActionsGlobalColumnsFields];

                    const isColumnDefaultValueSet = globalColumnsDefaultValues && Boolean(bulkActionsGlobalColumnsParser({
                      [columnKey]: globalColumnsDefaultValues[columnKey as keyof typeof bulkActionsGlobalColumnsFields]
                    } as BulkActionGlobalColumnsValidationSchema)[columnKey]);

                    return (
                      <div key={columnKey}>
                        <FieldComponent
                          {...entryInspect.global_columns[columnKey]}
                          disabledFromDefaultValue={isColumnDefaultValueSet}
                        />
                      </div>
                    );
                  })}

                  <div className="mt-auto w-full">
                    {entryMyLatestInProgress.id ? (
                      <Modal
                        triggerAsChild
                        Trigger={(
                          <Button className="w-full text-white-800">
                            Iniciar nova ação em massa
                          </Button>
                        )}
                        onConfirm={() => {
                          setSelectedEntry(entryMyLatestInProgress.id);
                          setSelectedActionerKlass(undefined);
                        }}
                        showClose={false}
                        subtitleClassName="!text-[15px]"
                        title=" "
                        subtitle="Você já tem uma ação em massa em andamento com estas configurações."
                        footer="Ver importação em andamento"
                      />
                    ) : (
                      <Button
                        className="w-full text-white-800"
                        onClick={handleSubmit(onSubmit)}
                      >
                        Iniciar nova ação em massa
                      </Button>
                    )}
                  </div>
                </div>

                <div className="w-1/2 flex flex-col">
                  <Table
                    columns={bulkActionsMineEntriesTableColumns}
                    rows={mineEntries || []}
                    page={1}
                    totalPages={1}
                    loadingRows={5}
                    isLoadingData={isMineEntriesPending}
                    rowEmptyValue="--"
                    headerClassName="bg-black"
                    className={(!mineEntries || !mineEntries.length) ? 'max-h-[300px]' : 'max-h-[468px]'}
                  />
                </div>
              </div>
            </div>
          </FormProvider>
        )}
      />

      <BulkActionTemplateModal
        entryId={selectedEntry}
        setEntryId={setSelectedEntry}
        setActionerKlassToOpenNewEntryModal={setSelectedActionerKlass}
        onEntryCompleted={onEntryCompleted}
      />
    </>
  );
};

export default BulkActionNewEntryModal;
